/**
 * ListingCard - Unified Dumb UI Component
 *
 * Diamond Architecture: Single listing card with variants.
 * Matches Rails CardComponent and Mobile ListingCard patterns.
 *
 * Unicorn Standard: Entity Component (src/components/listings)
 * Feature Modules should never import from each other.
 * This shared component lives at the Entity Layer for reuse by
 * both Storefront and Account feature modules.
 *
 * Variants:
 * - full: Grid/carousel view (title, location, basic info, price)
 * - compact: Minimal view (basic info, price only)
 *
 * Context:
 * - browse: For browsing listings - shows save button
 * - account: Owner's My Listings - shows the badge the PAGE decided
 *
 * This is a "dumb" presentational component that works everywhere:
 * - Server Components render it (hydrates on client)
 * - Client Components render it (already on client)
 *
 * ONE file. DRY. Unicorn Standard.
 */
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { formatPrice } from '@letsgethome/lib/currency';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import type { ListingStatusBadge } from '@letsgethome/lib/wasm';
import type { Route } from '@letsgethome/routes';
import { plainText, plaintextString } from '@letsgethome/types/branded';
import { CardControlLayer, CardSurface } from '@letsgethome/ui/card-layers';
import { cn } from '@letsgethome/ui/lib/utils';
import { S3Image } from '@letsgethome/ui/s3-image';

import { CardCarousel } from './CardCarousel';
import { CardOverlayLink } from './CardOverlayLink';
import { getBasicInfoParts, getDisplayTitle, getListingPriceData } from './listing-utils';
import type { ListingCardCore } from './ListingCard.types';
import { SaveButton } from './SaveButton';
import { KIND_CLASS, LABEL_KEY } from './status-badge-tokens';

// ----------------------------------------------------------------------------
// StatusBadge - Account context status indicator
// Matches Mobile ListingCard StatusBadge (Fractal Symmetry)
// ----------------------------------------------------------------------------

// ★ THE CARD NO LONGER DECIDES (D2). It used to take a raw `status` and call
// `listingStatusBadge` itself — which is the STATUS-ONLY decision, and on the
// owner's own surface that is the wrong question: a listing whose translation
// FAILED is `PUBLISHING` like one that is merely still translating, so the
// status-only badge said "Going live shortly" forever. The owner's badge is
// `listing_owner_badge(status, translation_state)`, and only the PAGE holds
// both inputs. So the decided `ListingStatusBadge` arrives on the account
// context and this component maps its two semantic outputs onto tokens —
// nothing more. The maps themselves moved to `./status-badge-tokens`, because
// the publish-hold notice tones itself from the same `kind` and two copies of
// a tone table is the drift this whole program keeps closing.
function StatusBadge({ badge }: { badge: ListingStatusBadge }) {
  const t = useTranslations('listings.card');
  const dotColor = KIND_CLASS[badge.kind];
  const label = t(LABEL_KEY[badge.label]);

  return (
    <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-xs font-medium px-2 py-1 rounded shadow-sm">
      <span className={cn('w-2 h-2 rounded-full', dotColor)} aria-hidden="true" />
      {label}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Types (matches Rails/Mobile)
// ----------------------------------------------------------------------------

export type ListingCardVariant = 'full' | 'compact';

/**
 * WHICH SURFACE this card is on — and, on the owner's, what control it
 * carries.
 *
 * ONE prop and a discriminated union, not two independent knobs, because the
 * owner's action control is meaningless anywhere else (LET-104): with a
 * separate `menu` prop beside a `'browse' | 'account'` string, a browse card
 * carrying a menu that silently rendered nothing was representable. It is not
 * now.
 *
 * The `menu` itself is a SLOT, not a set of props: the card is a dumb entity
 * component in the shared layer and the control's contents come from the
 * account feature's lifecycle SSOT. What the CARD owns is the one thing a
 * caller must not decide for itself — WHERE the control sits and which LAYER
 * it paints on. It goes in a `CardControlLayer` at `right-2 top-2`, the same
 * seam and the same corner the browse card's save heart uses, because a
 * control inside a card that is one big stretched link is a HIT-TEST decision
 * (LET-85): under the overlay link it still renders, still hovers, and every
 * click on it navigates instead.
 *
 * ★ `badge` is REQUIRED on the account arm and is a DECISION, not an input
 * (D2). The badge an owner should see is `listing_owner_badge(status,
 * translation_state)` — two inputs, only one of which a `ListingCardCore` ever
 * carried — so while the card computed its own from an optional `status` it
 * was free to answer the wrong question, and did: every failed translation
 * rendered "Going live shortly". The page evaluates the SSOT once and hands
 * the answer over; there is no longer a representable account card that
 * decides its own badge, and none that shows no badge at all.
 */
export type ListingCardContext =
  | { readonly kind: 'browse' }
  | {
      readonly kind: 'account';
      readonly badge: ListingStatusBadge;
      readonly menu?: React.ReactNode;
    };

/** The default surface — a card nobody said anything about is a browse card. */
const BROWSE_CARD: ListingCardContext = { kind: 'browse' };

interface ListingCardBaseProps {
  // `ListingCardCore`, not `ListingCardData`: the card renders no publicId/slug
  // and takes its `href` as a prop, so it works for a draft (which has no public
  // URL) and a published listing alike.
  listing: ListingCardCore;
  variant?: ListingCardVariant;
  context?: ListingCardContext;
  transactionType?: TransactionModeValue;
  className?: string;
  /**
   * The map-sync affordance on a map/list surface: the reader is now ON this
   * card, or no longer is. The hub uses it to highlight the listing's price
   * pin.
   *
   * It is not a CLICK, and that distinction is the whole of obs. 4. While the
   * sync rode on `onClick`, the card had to consume the first click to select —
   * so a card, which is a link, took two clicks to open.
   *
   * ★ "On this card" is the POINTER **or** the KEYBOARD, which is why it is not
   * called `onHoverChange` any more. The old name was accurate and the
   * behaviour it named was pointer-only: tabbing through the results list
   * highlighted no pin at all, an a11y gap with no error anywhere. Both
   * channels arrive through `CardSurface`, the card ROOT, which also DROPS the
   * `touch` pointer type — a tap fires `pointerenter` on touchdown and
   * `pointerleave` on lift, so an unguarded pair ran this handler twice,
   * mid-tap, on every tablet.
   */
  onHighlightChange?: (highlighted: boolean) => void;
  /** Responsive sizes attribute for next/image srcset selection */
  sizes: string;
}

/**
 * A card's ADDRESSABILITY is its whole activation story (LET-83, then obs. 4).
 *
 * LET-83 closed the first half: a `role="button"` div with an `onClick` and no
 * `href` was representable, and the listings hub was what it looked like —
 * `/listings/rent` and `/listings/buy` emitted ZERO `<a href>` to any listing
 * detail page at any amount of seeded data, so the whole detail corpus was
 * unreachable from the surface a crawler enters through, and a human got no
 * middle-click, no open-in-new-tab, no copy-link and nothing at all before
 * React hydrated. The union made "a click handler without a URL" a type error.
 *
 * ★ `onClick` IS GONE ENTIRELY, and that closes the other half. A handler the
 * hub could pass was a handler the hub could pass INSTEAD of navigating: the
 * card fed it to `CardOverlayLink`'s `onActivate`, `EnhancedLink` therefore
 * `preventDefault()`ed the plain primary click, and the hub spent that click on
 * selecting the card rather than opening it. Measured at both viewports: click
 * one left the URL unchanged and drew a ring; only click two navigated. With no
 * `onClick` in the union, a card whose click is hijacked is not representable —
 * and `CardOverlayLink` no longer offers `onActivate` at all, because this was
 * its only consumer. A surface that wants to know about the POINTER (the map
 * pin sync) says so with `onHighlightChange`, which cannot touch the click.
 */
type ListingCardActivation = { href?: undefined } | { href: Route };

export type ListingCardProps = ListingCardBaseProps & ListingCardActivation;

// ----------------------------------------------------------------------------
// Formatting Helper
// ----------------------------------------------------------------------------

function useFormattedBasicInfo(listing: ListingCardCore) {
  const t = useTranslations('listings.card');
  const { bedrooms, bathrooms, area, isStudio } = getBasicInfoParts(listing);

  const parts: string[] = [];
  if (isStudio) {
    parts.push(t('bedroomsStudio'));
  } else if (bedrooms !== null && bedrooms > 0) {
    parts.push(t('bedrooms', { count: bedrooms }));
  }
  if (bathrooms !== null && bathrooms > 0) {
    parts.push(t('bathrooms', { count: bathrooms }));
  }
  if (area) {
    parts.push(t('sqft', { area }));
  }
  return parts.join(' · ');
}

// ----------------------------------------------------------------------------
// Image Section
// ----------------------------------------------------------------------------

function CardImageSection({
  listing,
  variant,
  context,
  displayTitle,
  sizes,
}: {
  listing: ListingCardCore;
  variant: ListingCardVariant;
  context: ListingCardContext;
  displayTitle: string;
  sizes: string;
}) {
  const t = useTranslations('listings.card');
  const firstImage = listing.imageKeys[0] ?? undefined;

  return (
    // `aspect-[4/3]` = the Rust SSOT `cardImageAspectRatio()` (build-time
    // Tailwind string; welded by card-aspect.weld.test.ts).
    <div className="aspect-[4/3] bg-muted relative rounded-lg overflow-hidden">
      {variant === 'full' ? (
        <CardCarousel
          images={listing.imageKeys}
          alt={plainText(displayTitle)}
          fallback={<span>{t('noImage')}</span>}
          sizes={sizes}
        />
      ) : firstImage ? (
        <S3Image
          src={firstImage}
          alt={plainText(displayTitle)}
          fill
          className="object-cover"
          sizes={sizes}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          {t('noImage')}
        </div>
      )}

      {context.kind === 'browse' && (
        <CardControlLayer className="right-2 top-2">
          <SaveButton saveable={{ kind: 'listing', id: listing.id }} serverSaved={listing.saved} />
        </CardControlLayer>
      )}

      {context.kind === 'account' && <StatusBadge badge={context.badge} />}

      {/* The owner's controls take the SAME corner and the SAME layer the
          browse context gives the save heart — one seam decides, so neither
          can end up under the stretched link. */}
      {context.kind === 'account' && context.menu && (
        <CardControlLayer className="right-2 top-2">{context.menu}</CardControlLayer>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function ListingCard({
  listing,
  variant = 'full',
  context = BROWSE_CARD,
  transactionType = 'rent',
  href,
  onHighlightChange,
  className,
  sizes,
}: ListingCardProps) {
  const locale = useLocale();

  // STRICT intent-scoped price (Rust SSOT): null = not offered for this
  // intent or unpriced — an upstream bug. Hide the price element; NEVER
  // substitute the other mode's price.
  const { price } = getListingPriceData(listing, transactionType);
  const formattedPrice = price === null ? null : formatPrice(price, locale);
  // Rust SSOT: never null/empty — falls back to the localized "Untitled Listing".
  const displayTitle = getDisplayTitle(listing, locale);
  const basicInfo = useFormattedBasicInfo(listing);
  // The second line is the MAPPER's decision (D9): absent on storefront cards,
  // whose qualified title already names the estate.
  const location = listing.locationLine ?? null;

  // THE ANCHOR IS THE WHOLE CONTRACT (LET-83, obs. 4). A crawler, a reader with
  // no JS, a middle-click, a keyboard user AND a hydrated plain click all get
  // the same thing: a navigation to `href`. Nothing intercepts it any more —
  // the card offers no way to, which is what makes "two clicks to open a card"
  // unrepresentable rather than merely fixed. `EnhancedLink` still owns which
  // clicks belong to the browser; it simply has nothing to hand anyone.
  //
  // ★ THE ROOT IS `CardSurface`, NOT A DIV (§2.13). `relative` and `group` used
  // to be spelled here, and on `EstateCard`, and NOT on the map popover's card —
  // three hand-rolled roots, one of which silently stopped revealing its
  // carousel arrows. The seam owns both now, and the arrows read their
  // visibility rule back out of it, so there is no longer a way to open a card
  // with a root that looks right and reveals nothing.
  //
  // ★ AND THE HIGHLIGHT GOES THROUGH THE SAME SEAM (obs. 2.15). This file used
  // to wire `onPointerEnter` / `onPointerLeave` itself. Both were wrong on
  // hardware nobody writing them is using — a pointer event is the union of
  // mouse, pen and TOUCH, so a tap fired the pair mid-tap on every tablet, and
  // there was no keyboard path at all. `CardSurface` takes the FACT
  // (`onHighlightChange`) and owns the filtering, because the card root is also
  // the only element whose `:hover` covers the whole card — the same argument
  // the reveal rule above rests on. `no-restricted-syntax` bans the raw
  // attributes in this app, so this is the only way to spell it.

  return (
    <CardSurface
      className={cn('w-full text-left transition-all', href && 'cursor-pointer', className)}
      onHighlightChange={onHighlightChange}
    >
      {/* The stretched link is a SIBLING of the carousel and the save heart,
          never their ancestor, and its layer comes from the card seam
          (LET-85): `CardOverlayLink` sits on `Z_LAYERS.cardOverlayLink`, every
          control on `Z_LAYERS.cardControls`, and the seam's own test is what
          says which is on top. */}
      {href && <CardOverlayLink href={href} className="rounded-lg" aria-label={displayTitle} />}

      <CardImageSection
        listing={listing}
        variant={variant}
        context={context}
        displayTitle={displayTitle}
        sizes={sizes}
      />

      <div className="pt-2">
        {variant === 'full' && (
          <>
            <p className="font-semibold text-foreground truncate">
              {plaintextString(displayTitle)}
            </p>
            {location && (
              <p className="text-sm text-muted-foreground truncate mt-0.5">
                {plaintextString(location)}
              </p>
            )}
          </>
        )}

        <div
          className={cn('flex items-baseline justify-between gap-2', variant === 'full' && 'mt-1')}
        >
          {basicInfo && (
            <p className="text-sm text-muted-foreground truncate">{plaintextString(basicInfo)}</p>
          )}
          {formattedPrice && (
            <p className="text-sm font-medium text-foreground whitespace-nowrap">
              {formattedPrice}
            </p>
          )}
        </div>
      </div>
    </CardSurface>
  );
}
